import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { resolveWhepResourceUrl } from './usePlayWhepSteam/usePlayWhepSteam.functions.ts';
import { useWebRtcStats } from './useWebRtcStats';

const INITIAL_RETRY_DELAY_MS = 2_000;
const MAX_RETRY_DELAY_MS = 60_000;
const DISCONNECTED_GRACE_MS = 4_000;
const STALL_TIMEOUT_MS = 10_000;
/** Upper bound on how long the peer connection stays open waiting for the session DELETE. */
const WHEP_DELETE_TIMEOUT_MS = 2_000;

interface UseWhepPlayerOptions {
  url: string; // WHEP endpoint
  streamUri?: string; // Original webRtcStreamUri for stats store key
  iceServers?: RTCIceServer[];
  headers?: Record<string, string>;
  jitterBufferTargetMs?: number;
  /** When false, tears down the WHEP session and skips connect/retry until re-enabled. */
  enabled?: boolean;
}

interface UseWhepPlayer {
  videoRef: RefObject<HTMLVideoElement | null>;
  start: () => Promise<void>;
  stop: () => void;
  state: RTCPeerConnectionState;
  error: string | null;
  errorCode: number | null;
  isLoading: boolean;
  retryCount: number;
}

export const useWhepPlayer = ({
  url,
  streamUri,
  iceServers,
  headers,
  jitterBufferTargetMs = 250,
  enabled = true,
}: UseWhepPlayerOptions): UseWhepPlayer => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const fetchAbortRef = useRef<AbortController | null>(null);
  const videoListenersRef = useRef<null | { cleanup: () => void }>(null);
  const startRef = useRef<(() => Promise<void>) | null>(null);
  const resourceUrlRef = useRef<string | null>(null);

  // Retry refs
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const disconnectedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryDelayRef = useRef(INITIAL_RETRY_DELAY_MS);
  const connectionIdRef = useRef(0);
  const lastProgressRef = useRef({ framesDecoded: 0, at: 0 });

  const [connectionState, setConnectionState] = useState<RTCPeerConnectionState>('new');
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [retryCount, setRetryCount] = useState(0);

  //region Attach media event listeners that mirror the native spinner
  const attachVideoListeners = useCallback((videoElement: HTMLVideoElement) => {
    // cleanup any previous
    videoListenersRef.current?.cleanup?.();

    // handlers
    const onLoadStart = () => {
      setIsLoading(true);
      setError(null);
    };
    const onWaiting = () => {
      setIsLoading(true);
      setError(null);
    };
    const onStalled = () => {
      setIsLoading(true);
    };

    const onLoadedData = () => {
      setIsLoading(false);
    };
    const onCanPlay = () => setIsLoading(false);
    const onPlaying = () => setIsLoading(false);

    const onEmptied = () => {
      setIsLoading(true);
    };
    const onError = () => {
      const err = videoElement.error;
      setError(err ? `MEDIA_ERR_${err.code}` : 'Video element error');
      setIsLoading(false);
    };

    videoElement.addEventListener('loadstart', onLoadStart);
    videoElement.addEventListener('waiting', onWaiting);
    videoElement.addEventListener('stalled', onStalled);

    videoElement.addEventListener('loadeddata', onLoadedData);
    videoElement.addEventListener('canplay', onCanPlay);
    videoElement.addEventListener('playing', onPlaying);

    videoElement.addEventListener('emptied', onEmptied);
    videoElement.addEventListener('error', onError);

    // initial state sync (in case events already fired)
    if (videoElement.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) {
      setIsLoading(true);
    }

    videoListenersRef.current = {
      cleanup: () => {
        videoElement.removeEventListener('loadstart', onLoadStart);
        videoElement.removeEventListener('waiting', onWaiting);
        videoElement.removeEventListener('stalled', onStalled);

        videoElement.removeEventListener('loadeddata', onLoadedData);
        videoElement.removeEventListener('canplay', onCanPlay);
        videoElement.removeEventListener('playing', onPlaying);

        videoElement.removeEventListener('emptied', onEmptied);
        videoElement.removeEventListener('error', onError);
      },
    };
  }, []);
  //endregion

  //region Timer management
  const cancelTimers = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (disconnectedTimerRef.current) {
      clearTimeout(disconnectedTimerRef.current);
      disconnectedTimerRef.current = null;
    }
  }, []);

  const scheduleRetry = useCallback(
    (generation: number, overrideDelayMs?: number) => {
      if (generation !== connectionIdRef.current) return;
      cancelTimers();

      const delay = overrideDelayMs ?? retryDelayRef.current;
      setRetryCount(c => c + 1);

      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        if (generation === connectionIdRef.current) {
          startRef.current?.();
        }
      }, delay);

      // Advance backoff for next time (unless server overrode the delay)
      if (overrideDelayMs == null) {
        retryDelayRef.current = Math.min(retryDelayRef.current * 2, MAX_RETRY_DELAY_MS);
      }
    },
    [cancelTimers]
  );
  //endregion

  //region Internal cleanup (connection resources only, no retry/state reset)
  const cleanupConnection = useCallback(() => {
    const pc = pcRef.current;

    try {
      fetchAbortRef.current?.abort();
    } catch (_err) {
      console.error('Error aborting fetch:', _err);
    }
    fetchAbortRef.current = null;

    if (pc) {
      try {
        pc.ontrack = null;
        pc.onconnectionstatechange = null;
        pc.getReceivers().forEach(r => r.track?.stop());
      } catch (_err) {
        console.error('Error stopping connection:', _err);
      }
      pcRef.current = null;
    }

    const closePeerConnection = () => {
      try {
        pc?.close();
      } catch (_err) {
        console.error('Error stopping connection:', _err);
      }
    };

    // Tell MediaMTX to tear down the server-side WHEP session (fire-and-forget). The peer
    // connection is closed only once the DELETE has settled: closing it first ends the session
    // on MediaMTX, so the DELETE would then answer 404.
    const resourceUrl = resourceUrlRef.current;
    if (resourceUrl) {
      resourceUrlRef.current = null;
      fetch(resourceUrl, {
        method: 'DELETE',
        signal: AbortSignal.timeout(WHEP_DELETE_TIMEOUT_MS),
      })
        .catch(() => {
          /* best-effort */
        })
        .finally(closePeerConnection);
    } else {
      closePeerConnection();
    }

    videoListenersRef.current?.cleanup?.();
    videoListenersRef.current = null;

    const el = videoRef.current;
    if (el && el.srcObject) {
      (el.srcObject as MediaStream | null)
        ?.getTracks?.()
        .forEach((t: MediaStreamTrack) => t.stop?.());
      el.srcObject = null;
    }
  }, []);
  //endregion

  //region stop connection (full stop — cancels retries)
  const stop = useCallback(() => {
    connectionIdRef.current++;
    cancelTimers();
    cleanupConnection();
    setError(null);
    setErrorCode(null);
    setIsLoading(false);
    setConnectionState('closed');
    setRetryCount(0);
    retryDelayRef.current = INITIAL_RETRY_DELAY_MS;
  }, [cancelTimers, cleanupConnection]);
  //endregion

  //region Stall check
  // A connection can stay 'connected' while no video gets through: on a congested link the
  // picture froze for minutes and only deactivating CameraControl brought it back. Treat a
  // connection that decodes no frame for a while as failed, and count it healthy only once frames
  // arrive.
  const onFramesDecoded = useCallback(
    (framesDecoded: number) => {
      const last = lastProgressRef.current;
      if (framesDecoded > last.framesDecoded) {
        lastProgressRef.current = { framesDecoded, at: Date.now() };
        retryDelayRef.current = INITIAL_RETRY_DELAY_MS;
        setRetryCount(0);
        return;
      }
      if (Date.now() - last.at < STALL_TIMEOUT_MS) return;

      setIsLoading(false);
      setConnectionState('failed');
      cleanupConnection();
      scheduleRetry(connectionIdRef.current);
    },
    [cleanupConnection, scheduleRetry]
  );

  useWebRtcStats({ pcRef, connectionState, streamUri, onFramesDecoded });
  //endregion

  //region start connection
  const start = useCallback(async () => {
    const connectionId = ++connectionIdRef.current;
    cancelTimers();
    cleanupConnection();
    setError(null);
    setErrorCode(null);
    setIsLoading(true);
    // Clear stale 'closed' from stop() so resume shows loading, not the error overlay.
    setConnectionState('new');

    const peerConnection = new RTCPeerConnection({ iceServers: iceServers || [] });
    pcRef.current = peerConnection;

    // we only want to receive video
    peerConnection.addTransceiver('video', { direction: 'recvonly' });

    peerConnection.ontrack = evt => {
      const [stream] = evt.streams;
      const videoElement = videoRef.current;
      if (!videoElement || !stream) return;
      videoElement.srcObject = stream;
      attachVideoListeners(videoElement); // <-- hook into native events
      // if autoplay is blocked, your UI can call el.play() on a user gesture

      const receiver = peerConnection.getReceivers().find(r => r.track.kind === 'video');
      if (receiver && 'jitterBufferTarget' in receiver) {
        receiver.jitterBufferTarget = jitterBufferTargetMs;
      }
    };

    peerConnection.onconnectionstatechange = () => {
      if (connectionId !== connectionIdRef.current) return;
      const st = peerConnection.connectionState;
      setConnectionState(st);

      if (st === 'connected') {
        // Backoff is reset once frames are decoded (see the stall check), not here: on a
        // congested link a connection can come up without any video getting through.
        lastProgressRef.current = { framesDecoded: 0, at: Date.now() };
        setError(null);
        setErrorCode(null);
        cancelTimers();
      } else if (st === 'disconnected') {
        // Transient — ICE may recover on its own. Wait before full reconnect.
        setError(`Connection is: ${st}`);
        if (!disconnectedTimerRef.current) {
          disconnectedTimerRef.current = setTimeout(() => {
            disconnectedTimerRef.current = null;
            if (connectionId !== connectionIdRef.current) return;
            if (peerConnection.connectionState !== 'connected') {
              setIsLoading(false);
              cleanupConnection();
              scheduleRetry(connectionId);
            }
          }, DISCONNECTED_GRACE_MS);
        }
      } else if (st === 'failed') {
        setIsLoading(false);
        setError(`Connection is: ${st}`);
        cleanupConnection();
        scheduleRetry(connectionId);
      } else if (st === 'closed') {
        setIsLoading(false);
        setError(`Connection is: ${st}`);
      }
    };

    let serverRetryDelayMs: number | undefined;
    try {
      // Create WebRTC offer
      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);

      const abortController = new AbortController();
      fetchAbortRef.current = abortController;

      const res = await fetch(url, {
        method: 'POST',
        signal: abortController.signal,
        headers: {
          'Content-Type': 'application/sdp',
          ...headers,
        },
        body: offer.sdp ?? '',
      });

      if (!res.ok) {
        // WHEP spec: 409 = no live stream yet, 503 = overloaded.
        // Both may include Retry-After header. Extract before throwing.
        const retryAfter = res.headers.get('Retry-After');
        const retryAfterSec = Number(retryAfter);
        serverRetryDelayMs = retryAfterSec > 0 ? retryAfterSec * 1000 : undefined;

        setErrorCode(res.status);
        throw new Error(`Server returned ${res.status} ${res.statusText}`);
      }

      // Store the WHEP resource URL so we can DELETE it on cleanup.
      const location = res.headers.get('Location');
      if (location) {
        resourceUrlRef.current = resolveWhepResourceUrl({ location, whepUrl: url });
      }

      const sdpAnswer = await res.text();

      await peerConnection.setRemoteDescription({ type: 'answer', sdp: sdpAnswer });

      // If the video is already ready, listeners will flip isLoading off.
    } catch (e) {
      if (connectionId !== connectionIdRef.current) return;
      if (e instanceof DOMException && e.name === 'AbortError') return;

      setError(e instanceof Error ? e.message : String(e));
      setConnectionState('failed');
      setIsLoading(false);
      cleanupConnection();
      scheduleRetry(connectionId, serverRetryDelayMs);
    } finally {
      fetchAbortRef.current = null;
    }
  }, [
    cancelTimers,
    cleanupConnection,
    scheduleRetry,
    iceServers,
    attachVideoListeners,
    url,
    headers,
    jitterBufferTargetMs,
  ]);
  //endregion

  // Keep startRef in sync so scheduleRetry can call the latest start
  useEffect(() => {
    startRef.current = start;
  }, [start]);

  useEffect(() => {
    if (!enabled) {
      stop();
      return;
    }
    start();
    return () => stop();
  }, [enabled, start, stop]);

  return {
    videoRef,
    start,
    stop,
    state: connectionState,
    error,
    errorCode,
    isLoading,
    retryCount,
  };
};
